/**
 * Sidebar — Two-column entity navigation (icon rail + menu panel)
 *
 * Left column (w-14): Category icon rail with accent color highlighting
 * Right column (w-[200px]): Expanded submenu showing entities in active category
 *
 * **100% DSL-driven**: All categories, items, icons, and order come from
 * LayoutConfig (fetched from GET /api/v1/config/current or auto-generated).
 */

import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ThemePatternBackground } from '@/components/ThemePatternBackground';
import { usePersistentLink } from '@/hooks/useNavParams';
import { useAppStore } from '@/stores/app-store';
import { MODULE_NAV_ITEMS, useLayoutConfigContext } from '@/lib/use-layout-config';
import { accentToThemePalette } from '@/lib/portal-theme';
import type { NavigationCategory, NavigationItem } from '@/lib/use-layout-config';
import { resolveThemedIcon } from '@/lib/icon-map';
import { useAuth } from '@/lib/auth';
import { isChatOnlyAccess } from '@/lib/chat-only-access';
import { useBrandThemeConfig } from '@/hooks/useBrandThemeConfig';
import { useVisibleNavigation } from '@/hooks/useVisibleNavigation';
import {
  recordDynamicNavigationNodeClick,
  useDynamicNavigationGraph,
  type DynamicNavigationVisibleItem,
} from '@/hooks/useDynamicNavigationGraph';
import { useNetworkItemBadges } from '@/hooks/useBooxSocket';
import {
  browserRouteForResolvedRuntimeRoute,
  resolvedDynamicRuntimeBindingForPath,
} from '@/lib/runtime-api';
import { Bot, ChevronLeft, ChevronRight, Shield } from 'lucide-react';
import { api } from '@/lib/api';

interface SidebarProps {
  collapsed: boolean;
  onNavigate?: (path: string, title: string, icon?: string) => void;
}

type RuntimeSidebarItem = NavigationItem & {
  dynamicBadgeRef?: string;
  dynamicBadgeValue?: number;
  dynamicBadgeTone?: string;
  dynamicBadgeHidden?: boolean;
};

const NAV_GROUP_LABELS_ZH: Record<string, string> = {
  overview: '总览',
  dashboard: '总览',
  overall: '总览',
  core: '核心',
  setup: '设置',
  inbound: '入库',
  qc: 'QC',
  stock: '库存',
  outbound: '出库',
  return: '退料',
  control: '管控',
  admin: '管理',
  ops: '运营',
  operations: '运营',
  contacts: '联系人',
  customer: '客户',
  relationship: '客户关系',
  sales: '销售',
  service: '服务',
  network: '网络',
  sourcing: '询价',
  reports: '报表',
  finance: '财务',
  transactions: '交易',
  banking: '银行',
  collections: '收付款',
  audit: '审计',
  resources: '资源',
  orders: '订单',
  tools: '工具',
  runtime: '运行时',
  build: '构建',
  configuration: '配置',
  integration: '集成',
  'ai-runtime': 'AI 运行时',
  governance: '治理',
  monitoring: '监控',
  system: '系统',
  systems: '系统',
  security: '安全',
  automation: '自动化',
  workspace: '工作台',
  data: '数据',
  ai: 'AI',
  sdk: 'SDK',
  'owner-control': '经营者控制',
};

function fallbackGroupLabel(group: string) {
  return group
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function resolveGroupLabel(t: ReturnType<typeof useTranslation>['t'], group: string, language?: string) {
  const normalized = group.toLowerCase();
  if (language?.toLowerCase().startsWith('zh')) {
    return NAV_GROUP_LABELS_ZH[normalized] ?? t(`group.${group}`, fallbackGroupLabel(group));
  }
  return t(`group.${group}`, fallbackGroupLabel(group));
}

function resolveCategoryLabel(
  t: ReturnType<typeof useTranslation>['t'],
  category: { id: string; label: string; labelKey?: string },
) {
  if (category.id.startsWith('cc-')) return category.label;
  return category.labelKey ? t(category.labelKey, category.label) : t(`category.${category.id}`, category.label);
}

function normalizeNavText(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function isOverviewNavItem(item: NavigationItem): boolean {
  const slug = normalizeNavText(item.slug);
  const id = normalizeNavText(item.id);
  const label = normalizeNavText(item.label);
  const labelPlural = normalizeNavText(item.labelPlural);
  return slug === 'overview'
    || slug.endsWith('-dashboard')
    || id.endsWith('-overview')
    || id.endsWith('-dashboard')
    || label === 'overview'
    || labelPlural === 'overview'
    || label === 'dashboard'
    || labelPlural === 'dashboard';
}

function isOverviewGroup(group: unknown): boolean {
  const normalized = normalizeNavText(group);
  return normalized === 'overview' || normalized === 'dashboard' || normalized === 'overall';
}

function overviewFirst<T extends NavigationItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ao = isOverviewNavItem(a);
    const bo = isOverviewNavItem(b);
    if (ao === bo) return 0;
    return ao ? -1 : 1;
  });
}

const WP_GROUP_ORDER: Record<string, number> = {
  overview: 0,
  dashboard: 0,
  overall: 0,
  inbound: 10,
  qc: 20,
  stock: 30,
  outbound: 40,
  return: 50,
  control: 60,
  setup: 90,
  admin: 90,
};

function isWpHiddenMenuItem(item: NavigationItem): boolean {
  return item.id === 'wp-product-stickers'
    || item.id === 'wp-documents'
    || item.route === '/wp/labels'
    || item.route === '/wp/documents';
}

function isWascoTenant(tenantSlug?: string | null): boolean {
  return String(tenantSlug ?? '').trim().toLowerCase() === 'wasco-malaysia';
}

function isNewRevoTenant(tenantSlug?: string | null): boolean {
  return String(tenantSlug ?? '').trim().toLowerCase() === 'new-revo';
}

function wpItemsWithSetupAtBottom<T extends NavigationItem>(items: T[]): T[] {
  return items
    .filter((item) => item.id !== 'wp-setup' && item.route !== '/wp/setup' && !isWpHiddenMenuItem(item))
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ao = isOverviewNavItem(a.item)
        ? 0
        : (WP_GROUP_ORDER[normalizeNavText(a.item.group)] ?? 50);
      const bo = isOverviewNavItem(b.item)
        ? 0
        : (WP_GROUP_ORDER[normalizeNavText(b.item.group)] ?? 50);
      if (ao === bo) return a.index - b.index;
      return ao - bo;
    })
    .map(({ item }) => item);
}

const CATEGORY_RAIL_DEFAULT_ROUTES: Record<string, string> = {
  home: '/',
  wp: '/wp',
  wm: '/wm',
  fm: '/fm',
  sc: '/sc',
  ai: '/ai',
  settings: '/settings/company',
};

function iconSetFrameClass(iconSet: string, surface: 'rail' | 'menu') {
  const base = surface === 'rail' ? 'h-7 w-7' : 'h-5 w-5';
  switch (iconSet) {
    case 'tabler':
      return `${base} rounded-[4px] border border-current/35 bg-transparent`;
    case 'phosphor':
      return `${base} rounded-full bg-current/15`;
    case 'streamline_free':
      return `${base} rounded-md bg-current/12 ring-1 ring-current/25 shadow-sm`;
    case 'lucide':
    default:
      return `${base}`;
  }
}

interface CompanyBranding {
  name?: string;
  slug?: string;
  logoUrl?: string;
}

function TenantCompanyMark({ branding, className }: { branding: CompanyBranding; className?: string }) {
  const name = branding.name?.trim() || branding.slug?.trim() || 'Company';
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'C';

  return (
    <div
      className={cn(
        'pointer-events-none absolute bottom-5 left-1/2 z-0 flex w-[60%] -translate-x-1/2 items-center justify-center',
        className,
      )}
      aria-hidden="true"
    >
      {branding.logoUrl ? (
        <img
          src={branding.logoUrl}
          alt=""
          className="max-h-24 w-full object-contain opacity-40 saturate-100"
        />
      ) : (
        <div className="flex aspect-square w-full max-w-28 items-center justify-center rounded-[2rem] border border-sidebar-foreground/10 bg-sidebar-foreground/[0.035] text-4xl font-black tracking-tight text-sidebar-foreground opacity-[0.08]">
          {initials}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, onNavigate }: SidebarProps) {
  const { config } = useLayoutConfigContext();
  const linkTo = usePersistentLink();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { activeCategory, setActiveCategory, toggleSidebar, chatPanelOpen, toggleChatPanel, accent, pwaVisualPattern, pwaIconSet } = useAppStore();
  const { user } = useAuth();
  const isWasco = isWascoTenant(user?.tenantSlug);
  const isNewRevo = isNewRevoTenant(user?.tenantSlug);
  const navigate = useNavigate();
  const chatOnlyAccess = isChatOnlyAccess(user);
  const isPlatformAdminShell = user?.role === 'platform_admin'
    || (typeof window !== 'undefined' && window.location.port === '3002');
  const { companyBranding } = useBrandThemeConfig({ enabled: !chatOnlyAccess });

  // Per-feature network unread dots (WS-driven, clears when route is active)
  const netBadges = useNetworkItemBadges();
  const netDotIds: Record<string, number> = {
    'net-messages':    netBadges.messages,
    'net-connections': netBadges.connections,
    'net-channels':    netBadges.channels,
  };

  // Navigation categories from DSL config — filtered by permission + module + WMS profile.
  // Tenant app shells keep command/system surfaces out of the primary business menu.
  // The isolated 3002 Command Control shell must show all CC governance categories.
  const NON_MENU_CATEGORY_IDS = useMemo(
    () => isPlatformAdminShell ? new Set<string>() : new Set(['cc-command', 'cc-systems']),
    [isPlatformAdminShell],
  );
  const visibleCategories = useVisibleNavigation(config.navigation, config.warehouseProfile);
  const routeFallbackCategory = useMemo<NavigationCategory | null>(() => {
    const isWpRoute = location.pathname === '/wp' || location.pathname.startsWith('/wp/');
    if (!isWpRoute || visibleCategories.some((category) => category.id === 'wp')) return null;
    return {
      id: 'wp',
      label: 'WP',
      labelKey: 'category.wp',
      icon: config.categoryIcons.wp ?? 'warehouse',
      order: 35,
      zone: 'verticals',
      requiredPermission: 'wp.read',
      requiredModule: 'WP',
      items: MODULE_NAV_ITEMS.wp ?? [],
    };
  }, [config.categoryIcons.wp, location.pathname, visibleCategories]);
  const visibleCategoriesForRoute = useMemo(
    () => routeFallbackCategory
      ? [...visibleCategories, routeFallbackCategory].sort((left, right) => left.order - right.order)
      : visibleCategories,
    [routeFallbackCategory, visibleCategories],
  );
  const menuCategories = useMemo(
    () => visibleCategoriesForRoute
      .filter((category) => !NON_MENU_CATEGORY_IDS.has(category.id))
      .filter((category) => !(isWasco && category.id === 'settings')),
    [visibleCategoriesForRoute, NON_MENU_CATEGORY_IDS, isWasco],
  );
  const fallbackHomeNav = useMemo<NavigationCategory>(() => ({
    id: 'home',
    label: t('category.home', 'Home'),
    labelKey: 'category.home',
    icon: 'home',
    order: 0,
    zone: 'core',
    requiredPermission: '*',
    items: MODULE_NAV_ITEMS.home ?? [],
  }), [t]);
  const activeNav = useMemo(
    () => visibleCategoriesForRoute.find((c) => c.id === activeCategory)
      ?? (activeCategory === 'home' ? fallbackHomeNav : undefined),
    [visibleCategoriesForRoute, activeCategory, fallbackHomeNav],
  );
  const pathDynamicRuntimeBinding = useMemo(
    () => resolvedDynamicRuntimeBindingForPath(location.pathname),
    [location.pathname, menuCategories],
  );
  const pathStaticCategory = useMemo(() => {
    const currentRoute = `${location.pathname}${location.search || ''}`;
    if (location.pathname === '/') {
      return {
        id: 'home',
        label: t('category.home', 'Home'),
        length: 1,
        matchRank: 3,
      };
    }
    let best: { id: string; label: string; length: number; matchRank: number } | null = null;
    for (const category of menuCategories) {
      for (const item of category.items) {
        const routePath = item.route.split('?')[0] || item.route;
        const exactRouteMatch = currentRoute === item.route;
        const exactPathMatch = location.pathname === routePath;
        const prefixMatch = routePath !== '/' && location.pathname.startsWith(`${routePath}/`);
        if (!exactRouteMatch && !exactPathMatch && !prefixMatch) continue;
        const matchRank = exactRouteMatch ? 3 : exactPathMatch ? 2 : 1;
        const bestRank: number = best?.matchRank ?? -1;
        const bestLength: number = best?.length ?? -1;
        if (matchRank < bestRank) continue;
        if (matchRank === bestRank && routePath.length <= bestLength) continue;
        best = {
          id: category.id,
          label: resolveCategoryLabel(t, category),
          length: routePath.length,
          matchRank,
        };
      }
    }
    return best;
  }, [location.pathname, location.search, t, menuCategories]);

  const isRouteActive = (route: string) => {
    const currentRoute = `${location.pathname}${location.search || ''}`;
    const routePath = route.split('?')[0] || route;
    const routeHasQuery = route.includes('?');

    if (routeHasQuery) return currentRoute === route;

    if (location.pathname === '/wp/inbound/new' || location.pathname === '/wm/inbound/new') {
      const mode = new URLSearchParams(location.search).get('mode');
      if (mode === 'RETURN' && (route === '/wp/inbound' || route === '/wm/inbound')) return false;
    }

    return currentRoute === route
      || location.pathname === routePath
      || (routePath !== '/' && location.pathname.startsWith(`${routePath}/`));
  };
  const dynamicRuntimeBinding = useMemo(
    () => pathDynamicRuntimeBinding ?? activeNav?.dynamicRuntime,
    [activeNav?.dynamicRuntime, pathDynamicRuntimeBinding],
  );
  const activePanelCategoryId = pathDynamicRuntimeBinding?.categoryId ?? pathStaticCategory?.id ?? activeNav?.id ?? '';
  const activePanelLabel = pathDynamicRuntimeBinding?.categoryLabel
    ?? pathStaticCategory?.label
    ?? (activeNav ? resolveCategoryLabel(t, activeNav) : '');
  const [wpMessageBadgeCount, setWpMessageBadgeCount] = useState(0);
  const [wpPendingApprovalBadgeCount, setWpPendingApprovalBadgeCount] = useState(0);
  const dynamicVisibleItems = useMemo<DynamicNavigationVisibleItem[]>(() => {
    if (!dynamicRuntimeBinding) return [];
    return menuCategories.flatMap((cat) =>
      cat.items
        .filter((item) => (
          dynamicRuntimeBinding.routePrefixes.some((prefix) => (
            item.route === prefix || item.route.startsWith(`${prefix}/`)
          ))
        ))
        .map((item) => ({
          id: item.id,
          route: item.route,
          label: item.labelPlural ?? item.label,
          categoryId: cat.id,
        })),
    );
  }, [dynamicRuntimeBinding, menuCategories]);
  const dynamicNav = useDynamicNavigationGraph({
    surface: 'web',
    module: dynamicRuntimeBinding?.module,
    profileTemplateCode: dynamicRuntimeBinding?.profileTemplateCode,
    locale: i18n.language,
    route: location.pathname,
    refreshKey: user?.tenantId,
    visibleItems: dynamicVisibleItems,
    enabled: !chatOnlyAccess && Boolean(dynamicRuntimeBinding),
  });

  // Auto-select first category if none active
  useEffect(() => {
    if (activeCategory === null && menuCategories.length > 0) {
      setActiveCategory(menuCategories[0]!.id);
    }
  }, [activeCategory, menuCategories, setActiveCategory]);

  useEffect(() => {
    const categoryId = pathDynamicRuntimeBinding?.categoryId ?? pathStaticCategory?.id;
    if (!categoryId || activeCategory === categoryId) return;
    if (!menuCategories.some((cat) => cat.id === categoryId)) return;
    setActiveCategory(categoryId);
  }, [activeCategory, pathDynamicRuntimeBinding?.categoryId, pathStaticCategory?.id, setActiveCategory, menuCategories]);

  // Active category's items
  const activeMenuItems = useMemo<RuntimeSidebarItem[]>(() => {
    if (activePanelCategoryId === 'ai' && activeNav) {
      return overviewFirst(activeNav.items);
    }
    if (dynamicRuntimeBinding && dynamicNav.graph?.nodes.length) {
      return overviewFirst(dynamicNav.graph.nodes.flatMap((node): RuntimeSidebarItem[] => {
        const route = typeof node.route === 'string' && node.route.length > 0 ? node.route : null;
        if (!route) return [];
        const dynamicBadge = node.badgeRef ? dynamicNav.badges[node.badgeRef] : undefined;
        return [{
          id: `profile-${node.id}`,
          slug: node.id,
          label: node.label,
          labelPlural: node.label,
          icon: node.icon,
          route: browserRouteForResolvedRuntimeRoute(route, dynamicRuntimeBinding),
          group: node.group,
          requiredPermission: node.requiredPermissions[0],
          dynamicBadgeRef: node.badgeRef,
          dynamicBadgeValue: dynamicBadge?.value,
          dynamicBadgeTone: dynamicBadge?.tone,
          dynamicBadgeHidden: dynamicBadge?.hidden,
        }];
      }));
    }
    if (!activeNav) return [];
    if (activeNav.id === 'wp') {
      return wpItemsWithSetupAtBottom(activeNav.items).map((item) => (
        item.id === 'wp-messages'
          ? { ...item, dynamicBadgeValue: wpMessageBadgeCount, dynamicBadgeTone: 'info' }
          : item.id === 'wp-pending-approvals'
            ? { ...item, dynamicBadgeValue: wpPendingApprovalBadgeCount, dynamicBadgeTone: 'warn' }
            : item
      ));
    }
    return overviewFirst(activeNav.items);
  }, [activeNav, activePanelCategoryId, dynamicNav.badges, dynamicNav.graph?.nodes, dynamicRuntimeBinding, wpMessageBadgeCount, wpPendingApprovalBadgeCount]);

  useEffect(() => {
    if (!menuCategories.some((category) => category.id === 'wp')) return undefined;
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const [messageRes, approvalRes] = await Promise.all([
          api.get<{ total: number }>('/api/v1/wms/messages', { page: 1, limit: 1 }),
          api.get<{ total: number }>('/api/v1/wms/pending-approvals', { page: 1, limit: 1, scope: 'all' }),
        ]);
        if (!cancelled) {
          setWpMessageBadgeCount(Number(messageRes?.total ?? 0));
          setWpPendingApprovalBadgeCount(Number(approvalRes?.total ?? 0));
        }
      } catch {
        if (!cancelled) {
          setWpMessageBadgeCount(0);
          setWpPendingApprovalBadgeCount(0);
        }
      }
    };
    void loadCounts();
    const timer = window.setInterval(() => { void loadCounts(); }, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [menuCategories]);

  const resolveItemLabel = (item: NavigationItem, mode: 'single' | 'plural' = 'plural') => {
    if (mode === 'plural') {
      if (item.labelPluralKey) return t(item.labelPluralKey, item.labelPlural);
      if (item.labelKey) return t(item.labelKey, item.labelPlural);
      return t(`nav.${item.labelPlural}`, item.labelPlural);
    }
    if (item.labelKey) return t(item.labelKey, item.label);
    return t(`nav.${item.label}`, item.label);
  };

  const resolveCategoryDefaultPath = (cat: (typeof menuCategories)[number]): string | undefined => (
    CATEGORY_RAIL_DEFAULT_ROUTES[cat.id]
    ?? cat.dynamicRuntime?.defaultRoute
    ?? cat.items.find((item) => item.route)?.route
    ?? MODULE_NAV_ITEMS[cat.id]?.find((item) => item.route)?.route
  );

  return (
    <>
      {/* Left Column — Category Icon Rail (always visible) */}
      <div className="flex w-14 flex-col items-center border-r bg-sidebar">
        {/* Logo area — click to navigate home */}
        <div className="flex h-12 w-full items-center justify-center border-b border-sidebar-border">
          <button
            onClick={() => {
              if (chatOnlyAccess) {
                navigate('/chat');
                return;
              }
              if (isWasco) {
                setActiveCategory('wp');
                navigate('/wp');
                return;
              }
              if (isNewRevo) {
                setActiveCategory('wm');
                navigate('/wm');
                return;
              }
              setActiveCategory('home');
              navigate('/');
            }}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white cursor-pointer transition-opacity hover:opacity-80"
            style={{ background: 'var(--category-indicator)' }}
            title={isPlatformAdminShell ? 'Boox Command Control' : t('category.home', 'Home')}
          >
            {isPlatformAdminShell ? <Shield className="h-[18px] w-[18px]" /> : t('app.logo')}
          </button>
        </div>

        {/* Spacer between logo and category icons */}
        <div className="mt-1" />

        {/* Category icons — DSL driven, split into 4 zones via cat.zone */}
        {!chatOnlyAccess && (() => {
          const coreCats     = menuCategories.filter((c) => c.zone === 'core');
          const verticalCats = menuCategories.filter((c) => c.zone === 'verticals');
          const supportCats  = menuCategories.filter((c) => c.zone === 'support');
          const adminCats    = menuCategories.filter((c) => c.zone === 'admin');
          // Categories without a zone fall into support (above admin)
          const unzonedCats  = menuCategories.filter((c) => !c.zone);

              const renderCatBtn = (cat: (typeof visibleCategoriesForRoute)[number]) => {
            const iconName = config.categoryIcons[cat.id] ?? cat.icon;
            const Icon = resolveThemedIcon(iconName, pwaIconSet);
            const isActive = cat.id === activeCategory;
            const label = resolveCategoryLabel(t, cat);
            const nextPath = resolveCategoryDefaultPath(cat);
            const content = (
              <>
                <span
                  className={cn(
                    'flex items-center justify-center transition-all',
                    iconSetFrameClass(pwaIconSet, 'rail'),
                  )}
                >
                  <Icon className="h-5 w-5" />
                </span>
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 category-indicator rounded-r-full" />
                )}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium text-white bg-foreground rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                    {label}
                  </span>
                )}
              </>
            );

            const baseClassName = cn(
              'relative group flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
              isActive
                ? 'category-active'
                : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            );

            const handleCategoryClick = () => {
              if (collapsed) toggleSidebar();
              if (nextPath) {
                onNavigate?.(nextPath, label, iconName);
                return;
              }
              setActiveCategory(cat.id);
            };

            if (nextPath) {
              return (
                <NavLink
                  key={cat.id}
                  to={linkTo(nextPath)}
                  reloadDocument
                  onClick={handleCategoryClick}
                  className={baseClassName}
                  title={label}
                  aria-label={label}
                >
                  {content}
                </NavLink>
              );
            }

            return (
              <button
                key={cat.id}
                type="button"
                onClick={handleCategoryClick}
                className={baseClassName}
                title={label}
                aria-label={label}
              >
                {content}
              </button>
            );
          };

          return (
            <>
              {/* Zone 1: Core — Home / Inbox / Business */}
              <nav className="flex flex-col gap-0.5 pb-1">
                {coreCats.map(renderCatBtn)}
              </nav>

              {/* Divider */}
              {coreCats.length > 0 && verticalCats.length > 0 && (
                <div className="w-7 border-t border-sidebar-border/60 my-1" />
              )}

              {/* Zone 2: Verticals — Transport / Freight / Warehouse */}
              <nav className="flex flex-col gap-0.5 py-1">
                {verticalCats.map(renderCatBtn)}
              </nav>

              {/* Divider */}
              {verticalCats.length > 0 && supportCats.length > 0 && (
                <div className="w-7 border-t border-sidebar-border/60 my-1" />
              )}

              {/* Zone 3: Support — Finance / Workforce + unzoned (flex-1 to push admin down) */}
              <nav className="flex flex-1 flex-col gap-0.5 py-1">
                {supportCats.map(renderCatBtn)}
                {unzonedCats.map(renderCatBtn)}
              </nav>

              {/* Zone 4: Admin — Settings / Platform (bottom-pinned) */}
              {adminCats.length > 0 && (
                <>
                  <div className="w-7 border-t border-sidebar-border/60 mb-1" />
                  <nav className="flex flex-col gap-0.5 mb-1">
                    {adminCats.map(renderCatBtn)}
                  </nav>
                </>
              )}
            </>
          );
        })()}

        {/* AI Chat toggle */}
        {!isWasco && !isNewRevo && (
          <button
            onClick={() => {
              if (chatOnlyAccess) {
                navigate('/chat');
                return;
              }
              toggleChatPanel();
            }}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
              (chatOnlyAccess ? location.pathname.startsWith('/chat') : chatPanelOpen)
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            )}
            title={t('nav.aiChat', 'AI Chat')}
          >
            <Bot className="h-5 w-5" />
          </button>
        )}

        {/* Collapse toggle */}
        {!chatOnlyAccess && (
          <button
            onClick={toggleSidebar}
            className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-sidebar-accent transition-colors"
            title={collapsed ? t('sidebar.expand', 'Expand') : t('sidebar.collapse', 'Collapse')}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Right Column — Menu Panel (hidden when collapsed) */}
      {!chatOnlyAccess && !collapsed && (activeNav || pathDynamicRuntimeBinding) && (
        <div
          className="relative flex w-[200px] flex-col overflow-hidden border-r border-sidebar-border bg-sidebar/50 shadow-[1px_0_0_rgba(15,23,42,0.04)]"
          data-dynamic-nav-status={dynamicNav.status}
          data-dynamic-nav-version={dynamicNav.graph?.version ?? ''}
          data-dynamic-nav-visible-count={dynamicNav.visibleCount}
          data-dynamic-nav-hidden-count={dynamicNav.hiddenCount}
          data-dynamic-nav-compare-status={dynamicNav.compareStatus}
          data-dynamic-nav-mismatch-count={dynamicNav.mismatchCount}
        >
          {/* Subtle logistics vector background — adapts to theme accent */}
          <ThemePatternBackground
            pattern={pwaVisualPattern}
            palette={accentToThemePalette(accent)}
            intensity="low"
            fit="contain"
            className="pointer-events-none absolute inset-0 h-full w-full opacity-45"
          />
          <TenantCompanyMark branding={companyBranding} />
          {/* Category header — aligned with TopBar h-12 */}
          <div className="flex h-12 items-center px-4 border-b border-sidebar-border relative z-10">
            <h2 className="text-sm font-semibold">
              {activePanelLabel}
            </h2>
          </div>

          {/* Menu items — grouped by NavigationItem.group (from DSL) */}
          <nav className="relative z-10 flex-1 overflow-y-auto px-2 pb-16 pt-2">
            {(() => {
              const groupedItems = groupByField(activeMenuItems);
              return groupedItems.map(({ group, items }) => (
                <div key={group ?? '__ungrouped'}>
                  {group && groupedItems.length > 1 && (
                    <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      {resolveGroupLabel(t, group, i18n.language)}
                    </div>
                  )}
                  {items.map((item) => {
                    const Icon = resolveThemedIcon(item.icon, pwaIconSet);
                    const path = item.route;
                    return (
                      <NavLink
                        key={item.id}
                        to={linkTo(path)}
                        onClick={() => {
                          onNavigate?.(path, resolveItemLabel(item, 'plural'), item.icon);
                          if (dynamicRuntimeBinding && (
                            dynamicRuntimeBinding.routePrefixes.some((prefix) => (
                              path === prefix || path.startsWith(`${prefix}/`)
                            ))
                          )) {
                            recordDynamicNavigationNodeClick({
                              surface: 'web',
                              module: dynamicRuntimeBinding.module,
                              profileTemplateCode: dynamicRuntimeBinding.profileTemplateCode,
                              locale: i18n.language,
                              route: location.pathname,
                              click: {
                                id: item.id,
                                route: path,
                                label: resolveItemLabel(item, 'plural'),
                                categoryId: activePanelCategoryId,
                                activeRoute: location.pathname,
                              },
                            });
                          }
                        }}
                        className={() =>
                          cn(
                            'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                            isRouteActive(path)
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
                          )
                        }
                      >
                        <span
                          className={cn(
                            'flex shrink-0 items-center justify-center transition-all',
                            iconSetFrameClass(pwaIconSet, 'menu'),
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="truncate flex-1">{resolveItemLabel(item, 'plural')}</span>
                        {typeof item.dynamicBadgeValue === 'number'
                          && item.dynamicBadgeValue > 0
                          && item.dynamicBadgeHidden !== true
                          && !location.pathname.startsWith(item.route) && (
                          <span
                            className={cn(
                              'ml-auto min-w-5 shrink-0 rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none',
                              item.dynamicBadgeTone === 'danger'
                                ? 'bg-red-600 text-white'
                                : item.dynamicBadgeTone === 'success'
                                  ? 'bg-emerald-600 text-white'
                                  : item.dynamicBadgeTone === 'info'
                                    ? 'bg-sky-600 text-white'
                                    : 'bg-amber-500 text-white',
                            )}
                            title={item.dynamicBadgeRef}
                          >
                            {item.dynamicBadgeValue > 99 ? '99+' : item.dynamicBadgeValue}
                          </span>
                        )}
                        {(netDotIds[item.id] ?? 0) > 0 && !location.pathname.startsWith(item.route) && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-primary shrink-0" />
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              ));
            })()}
          </nav>
        </div>
      )}

      {/* Right Column — Legacy Admin Panel (kept for backward compat, triggered by __admin) */}
      {!collapsed && activeCategory === '__admin' && (
        <AdminPanel onNavigate={onNavigate} companyBranding={companyBranding} />
      )}
    </>
  );
}

/* Helpers */

/**
 * Admin sidebar panel — reads items from the 'platform' / 'settings' navigation
 * categories in DSL config, rather than a hardcoded list.
 */
function AdminPanel({ onNavigate, companyBranding }: { onNavigate?: (path: string, title: string, icon?: string) => void; companyBranding: CompanyBranding }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { config } = useLayoutConfigContext();
  const location = useLocation();
  const linkTo = usePersistentLink();
  const pwaVisualPattern = useAppStore((s) => s.pwaVisualPattern);
  const pwaIconSet = useAppStore((s) => s.pwaIconSet);
  const isPlatformAdmin = user?.role === 'platform_admin';

  // Collect items from admin-zone categories in the nav config
  const adminItems = useMemo(() => {
    const items: NavigationItem[] = [];
    for (const cat of config.navigation) {
      if (cat.zone !== 'admin') continue;
      for (const item of cat.items) {
        items.push({
          ...item,
          group: item.group ?? cat.label,
        });
      }
    }
    return items;
  }, [config.navigation]);

  const grouped = useMemo(() => groupByField(adminItems), [adminItems]);
  const isRouteActive = (route: string) => {
    const currentRoute = `${location.pathname}${location.search || ''}`;
    const routePath = route.split('?')[0] || route;
    return currentRoute === route
      || location.pathname === routePath
      || (routePath !== '/' && location.pathname.startsWith(`${routePath}/`));
  };
  const resolveItemLabel = (item: NavigationItem, mode: 'single' | 'plural' = 'plural') => {
    if (mode === 'plural') {
      if (item.labelPluralKey) return t(item.labelPluralKey, item.labelPlural);
      if (item.labelKey) return t(item.labelKey, item.labelPlural);
      return t(`nav.${item.labelPlural}`, item.labelPlural);
    }
    if (item.labelKey) return t(item.labelKey, item.label);
    return t(`nav.${item.label}`, item.label);
  };

  return (
    <div className="relative flex w-[200px] flex-col overflow-hidden border-r border-sidebar-border bg-sidebar/50 shadow-[1px_0_0_rgba(15,23,42,0.04)]">
      <ThemePatternBackground
        pattern={pwaVisualPattern}
        palette="slate"
        intensity="low"
        fit="contain"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-45"
      />
      <TenantCompanyMark branding={companyBranding} />
      <div className="flex h-12 items-center px-4 border-b border-sidebar-border relative z-10">
        <h2 className="text-sm font-semibold">{t('admin.title', 'AI 管理')}</h2>
      </div>
      <nav className="relative z-10 flex-1 overflow-y-auto px-2 pb-16 pt-2">
        {grouped.map(({ group, items }) => (
          <div key={group ?? '__ungrouped'}>
            {group && (
              <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {resolveGroupLabel(t, group, i18n.language)}
              </div>
            )}
            {items.map((item) => {
              const Icon = resolveThemedIcon(item.icon, pwaIconSet);
              return (
                <NavLink
                  key={item.id}
                  to={linkTo(item.route)}
                  onClick={() => onNavigate?.(item.route, resolveItemLabel(item, 'plural'), item.icon)}
                  className={() =>
                    cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                      isRouteActive(item.route)
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
                    )
                  }
                >
                  <span
                    className={cn(
                      'flex shrink-0 items-center justify-center transition-all',
                      iconSetFrameClass(pwaIconSet, 'menu'),
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="truncate">{resolveItemLabel(item, 'plural')}</span>
                </NavLink>
              );
            })}
          </div>
        ))}

        {/* ── Platform DevOps (boox.app staff only) ── */}
        {isPlatformAdmin && (
          <div className="mt-2 border-t border-sidebar-border pt-2">
            <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-purple-500/80">
              Platform
            </div>
            <NavLink
              to={linkTo('/admin/platform')}
              onClick={() => onNavigate?.('/admin/platform', 'Platform DevOps', 'shield')}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-purple-100 text-purple-700 font-medium'
                    : 'text-purple-600 hover:bg-purple-50',
                )
              }
            >
              <Shield className="h-4 w-4 shrink-0" />
              <span className="truncate">DevOps Console</span>
            </NavLink>
          </div>
        )}
      </nav>
    </div>
  );
}

/** Group items within a category by their `group` field, preserving order */
function groupByField<T extends NavigationItem>(items: T[]): { group: string | undefined; items: T[] }[] {
  const groups: { group: string | undefined; items: T[] }[] = [];
  const seen = new Map<string | undefined, number>();
  for (const item of overviewFirst(items)) {
    const g = isOverviewNavItem(item) && isOverviewGroup(item.group) ? undefined : item.group;
    const idx = seen.get(g);
    if (idx !== undefined) {
      groups[idx]!.items.push(item);
    } else {
      seen.set(g, groups.length);
      groups.push({ group: g, items: [item] });
    }
  }
  return groups;
}
